<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { ImageOff, LoaderCircle, Search } from 'lucide-vue-next'
import AppModal from '@/components/AppModal.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import SiteHeader from '@/components/SiteHeader.vue'
import { novaApi, type ProjectDetail, type ProjectListParams, type ProjectSummary } from '@/services/api'

type LoadState = 'loading' | 'ready' | 'empty' | 'error'
type DetailState = 'idle' | 'loading' | 'ready' | 'error'

const previewProjects: ProjectSummary[] = [
  {
    id: -101,
    title: '品牌短视频系列制作',
    categoryName: '广告/营销',
    budgetMin: 30000,
    budgetMax: 80000,
    viewCount: 1248,
    applicationCount: 86,
    mediaUrls: ['/videos/demo/posters/hero-city.jpg'],
    createTime: '2024-06-01T10:00:00',
    acceptingBid: true,
    publisherNickname: '光影行者',
  },
  {
    id: -102,
    title: '智能制造企业宣传片',
    categoryName: '企业宣传',
    budgetMin: 80000,
    budgetMax: 150000,
    viewCount: 2356,
    applicationCount: 112,
    mediaUrls: ['/videos/demo/posters/enterprise-robot.jpg'],
    createTime: '2024-05-30T10:00:00',
    acceptingBid: true,
    publisherNickname: '智造未来',
  },
  {
    id: -103,
    title: '产品演示视频',
    categoryName: '3C/数码',
    budgetMin: 20000,
    budgetMax: 50000,
    viewCount: 987,
    applicationCount: 64,
    mediaUrls: ['/videos/demo/posters/product-skincare.jpg'],
    createTime: '2024-05-28T10:00:00',
    acceptingBid: true,
    publisherNickname: '设计视界',
  },
  {
    id: -104,
    title: '课程内容剪辑',
    categoryName: '教育/培训',
    budgetMin: 10000,
    budgetMax: 30000,
    viewCount: 756,
    applicationCount: 43,
    mediaUrls: ['/videos/demo/posters/course-presentation.jpg'],
    createTime: '2024-05-27T10:00:00',
    acceptingBid: true,
    publisherNickname: '知识光影',
  },
]

const query = ref('')
const industry = ref('')
const budget = ref('all')
const orderBy = ref<ProjectListParams['orderBy']>('createTime')
const loadState = ref<LoadState>('loading')
const projects = ref<ProjectSummary[]>([])
const total = ref(0)
const knownIndustries = ref<string[]>([])
const previewMode = ref(false)
const errorMessage = ref('')
const imageFailures = ref(new Set<string>())
const selectedProject = ref<ProjectSummary | null>(null)
const projectDetail = ref<ProjectDetail | null>(null)
const detailState = ref<DetailState>('idle')
const detailError = ref('')
let requestSerial = 0

const budgetOptions = [
  { value: 'all', label: '预算范围' },
  { value: 'under-10000', label: '1 万元以下' },
  { value: '10000-50000', label: '1 万至 5 万元' },
  { value: 'over-50000', label: '5 万元以上' },
]

const industryOptions = computed(() => knownIndustries.value)
const featuredProject = computed(() => projects.value[0])
const projectRows = computed(() => projects.value.slice(1))

const budgetParams = (): Pick<ProjectListParams, 'budgetMin' | 'budgetMax'> => {
  if (budget.value === 'under-10000') return { budgetMax: 10000 }
  if (budget.value === '10000-50000') return { budgetMin: 10000, budgetMax: 50000 }
  if (budget.value === 'over-50000') return { budgetMin: 50000 }
  return {}
}

const filterPreviewProjects = () => {
  const keyword = query.value.trim().toLowerCase()
  const list = previewProjects.filter((project) => {
    const min = Number(project.budgetMin) || 0
    const max = Number(project.budgetMax) || min
    const matchesKeyword = !keyword || `${project.title}${project.categoryName ?? ''}${project.publisherNickname ?? ''}`.toLowerCase().includes(keyword)
    const matchesIndustry = !industry.value || project.categoryName === industry.value
    const matchesBudget = budget.value === 'all'
      || (budget.value === 'under-10000' && min <= 10000)
      || (budget.value === '10000-50000' && max >= 10000 && min <= 50000)
      || (budget.value === 'over-50000' && max >= 50000)
    return matchesKeyword && matchesIndustry && matchesBudget
  })
  return list.sort((left, right) => {
    if (orderBy.value === 'viewCount') return Number(right.viewCount) - Number(left.viewCount)
    if (orderBy.value === 'applicationCount') return Number(right.applicationCount) - Number(left.applicationCount)
    return new Date(right.createTime ?? 0).getTime() - new Date(left.createTime ?? 0).getTime()
  })
}

const loadProjects = async () => {
  const currentRequest = ++requestSerial
  loadState.value = 'loading'
  errorMessage.value = ''
  try {
    const result = await novaApi.getProjects({
      pageNo: 1,
      pageSize: 12,
      keyword: query.value.trim() || undefined,
      industryName: industry.value || undefined,
      orderBy: orderBy.value,
      orderDirection: 'desc',
      ...budgetParams(),
    })
    if (currentRequest !== requestSerial) return
    projects.value = Array.isArray(result.list) ? result.list : []
    knownIndustries.value = Array.from(new Set([
      ...knownIndustries.value,
      ...projects.value.map((project) => project.categoryName?.trim()).filter((value): value is string => Boolean(value)),
    ]))
    total.value = Number(result.total) || projects.value.length
    previewMode.value = false
    loadState.value = projects.value.length ? 'ready' : 'empty'
  } catch (error) {
    if (currentRequest !== requestSerial) return
    if (import.meta.env.DEV) {
      projects.value = filterPreviewProjects()
      knownIndustries.value = Array.from(new Set(previewProjects.map((project) => project.categoryName).filter((value): value is string => Boolean(value))))
      total.value = projects.value.length
      previewMode.value = true
      loadState.value = projects.value.length ? 'ready' : 'empty'
      return
    }
    projects.value = []
    total.value = 0
    loadState.value = 'error'
    errorMessage.value = error instanceof Error ? error.message : '项目数据暂时无法加载'
  }
}

const formatNumber = (value?: number) => new Intl.NumberFormat('zh-CN').format(Number(value) || 0)
const formatMoney = (value?: number | string) => new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 }).format(Number(value) || 0)
const formatBudget = (project: ProjectSummary | ProjectDetail) => {
  const min = Number(project.budgetMin)
  const max = Number(project.budgetMax)
  if (Number.isFinite(min) && Number.isFinite(max) && (min > 0 || max > 0)) return `¥${formatMoney(min)} – ¥${formatMoney(max)}`
  if (Number.isFinite(min) && min > 0) return `¥${formatMoney(min)} 起`
  if (Number.isFinite(max) && max > 0) return `¥${formatMoney(max)} 以内`
  return '预算面议'
}
const publisherName = (project: ProjectSummary) => project.publisherNickname?.trim() || '项目发布方'
const publisherInitial = (project: ProjectSummary) => publisherName(project).slice(0, 1)
const projectCover = (project: ProjectSummary) => project.mediaUrls?.find(Boolean)
const failed = (key: string) => imageFailures.value.has(key)
const markImageFailed = (key: string) => {
  imageFailures.value = new Set(imageFailures.value).add(key)
}

const openProject = async (project: ProjectSummary) => {
  selectedProject.value = project
  projectDetail.value = null
  detailState.value = 'loading'
  detailError.value = ''
  if (previewMode.value && project.id < 0) {
    projectDetail.value = {
      ...project,
      description: '当前为本地界面预览，正式项目说明将在接口连接后显示。',
    }
    detailState.value = 'ready'
    return
  }
  try {
    projectDetail.value = await novaApi.getProject(project.id)
    detailState.value = 'ready'
  } catch (error) {
    detailState.value = 'error'
    detailError.value = error instanceof Error ? error.message : '项目详情暂时无法加载'
  }
}

const closeProject = () => {
  selectedProject.value = null
  projectDetail.value = null
  detailState.value = 'idle'
}

onMounted(loadProjects)
</script>

<template>
  <div class="task-market-page">
    <SiteHeader />
    <main class="task-market-main">
      <div class="task-market-shell">
        <header class="task-market-intro">
          <div>
            <h1>接单广场</h1>
            <p>寻找适合你的创作项目</p>
          </div>
          <div class="task-market-meta">
            <small v-if="previewMode" class="task-preview-badge" role="status">当前显示本地预览数据</small>
            <span aria-live="polite">共 {{ formatNumber(total) }} 个项目</span>
          </div>
        </header>

        <form class="task-filter-bar" role="search" @submit.prevent="loadProjects">
          <label class="task-search-field">
            <Search :size="19" aria-hidden="true" />
            <input v-model="query" type="search" placeholder="搜索项目" aria-label="搜索项目" />
          </label>
          <select v-model="industry" aria-label="筛选行业" @change="loadProjects">
            <option value="">全部行业</option>
            <option v-for="item in industryOptions" :key="item" :value="item">{{ item }}</option>
          </select>
          <select v-model="budget" aria-label="筛选预算" @change="loadProjects">
            <option v-for="item in budgetOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
          <select v-model="orderBy" aria-label="项目排序" @change="loadProjects">
            <option value="createTime">最新发布</option>
            <option value="viewCount">最多浏览</option>
            <option value="applicationCount">最多申请</option>
          </select>
          <button type="submit">查询项目</button>
        </form>

        <section v-if="loadState === 'loading'" class="task-board-status" aria-live="polite">
          <LoaderCircle :size="30" aria-hidden="true" />
          <strong>正在加载开放项目</strong>
        </section>

        <section v-else-if="loadState === 'error'" class="task-board-status" role="alert">
          <strong>项目数据暂时无法加载</strong>
          <p>{{ errorMessage }}</p>
          <button type="button" @click="loadProjects">重新加载</button>
        </section>

        <section v-else-if="loadState === 'empty'" class="task-board-status" role="status">
          <strong>暂无符合条件的开放项目</strong>
          <p>调整关键词、行业或预算范围后再试。</p>
        </section>

        <section v-else class="project-board" aria-label="开放项目列表">
          <article v-if="featuredProject" class="project-featured">
            <div class="project-featured-media">
              <img
                v-if="projectCover(featuredProject) && !failed(`cover-${featuredProject.id}`)"
                :src="projectCover(featuredProject)"
                :alt="`${featuredProject.title} 项目封面`"
                @error="markImageFailed(`cover-${featuredProject.id}`)"
              />
              <span v-else class="project-media-fallback"><ImageOff :size="34" aria-hidden="true" /><small>项目暂未提供封面</small></span>
            </div>
            <div class="project-featured-copy">
              <div class="project-publisher">
                <span class="project-avatar">
                  <img
                    v-if="featuredProject.publisherAvatar && !failed(`avatar-${featuredProject.id}`)"
                    :src="featuredProject.publisherAvatar"
                    alt=""
                    @error="markImageFailed(`avatar-${featuredProject.id}`)"
                  />
                  <b v-else>{{ publisherInitial(featuredProject) }}</b>
                </span>
                <strong>{{ publisherName(featuredProject) }}</strong>
              </div>
              <h2>{{ featuredProject.title }}</h2>
              <p>{{ featuredProject.categoryName || '暂未分类' }}</p>
              <div class="project-budget"><span>预算范围</span><strong>{{ formatBudget(featuredProject) }}</strong></div>
            </div>
            <aside class="project-featured-aside">
              <dl>
                <div><dt>浏览</dt><dd>{{ formatNumber(featuredProject.viewCount) }}</dd></div>
                <div><dt>申请</dt><dd>{{ formatNumber(featuredProject.applicationCount) }}</dd></div>
              </dl>
              <strong :class="featuredProject.acceptingBid ? 'project-open' : 'project-closed'">{{ featuredProject.acceptingBid ? '可申请' : '已结束' }}</strong>
              <button type="button" @click="openProject(featuredProject)">查看项目</button>
            </aside>
          </article>

          <div v-if="projectRows.length" class="project-card-grid">
            <article v-for="project in projectRows" :key="project.id" class="project-card">
              <div class="project-card-media">
                <img
                  v-if="projectCover(project) && !failed(`cover-${project.id}`)"
                  :src="projectCover(project)"
                  :alt="`${project.title} 项目封面`"
                  @error="markImageFailed(`cover-${project.id}`)"
                />
                <span v-else class="project-media-fallback"><ImageOff :size="24" aria-hidden="true" /><small>暂无封面</small></span>
              </div>
              <div class="project-card-body">
                <div class="project-publisher compact">
                  <span class="project-avatar">
                    <img v-if="project.publisherAvatar && !failed(`avatar-${project.id}`)" :src="project.publisherAvatar" alt="" @error="markImageFailed(`avatar-${project.id}`)" />
                    <b v-else>{{ publisherInitial(project) }}</b>
                  </span>
                  <strong>{{ publisherName(project) }}</strong>
                </div>
                <h2>{{ project.title }}</h2>
                <p>{{ project.categoryName || '暂未分类' }}</p>
                <dl class="project-card-metrics">
                  <div><dt>浏览</dt><dd>{{ formatNumber(project.viewCount) }}</dd></div>
                  <div><dt>申请</dt><dd>{{ formatNumber(project.applicationCount) }}</dd></div>
                </dl>
                <div class="project-card-budget"><span>预算范围</span><strong>{{ formatBudget(project) }}</strong></div>
                <div class="project-card-action">
                  <strong :class="project.acceptingBid ? 'project-open' : 'project-closed'">{{ project.acceptingBid ? '可申请' : '已结束' }}</strong>
                  <button type="button" @click="openProject(project)">查看项目</button>
                </div>
              </div>
            </article>
          </div>
        </section>
      </div>
    </main>
    <SiteFooter />

    <AppModal v-if="selectedProject" :title="selectedProject.title" @close="closeProject">
      <div class="project-detail-modal">
        <p v-if="detailState === 'loading'" class="project-detail-state">正在加载项目详情…</p>
        <p v-else-if="detailState === 'error'" class="project-detail-state error" role="alert">{{ detailError }}</p>
        <template v-else-if="projectDetail">
          <dl>
            <div><dt>行业</dt><dd>{{ projectDetail.categoryName || '暂未分类' }}</dd></div>
            <div><dt>预算</dt><dd>{{ formatBudget(projectDetail) }}</dd></div>
            <div><dt>浏览</dt><dd>{{ formatNumber(projectDetail.viewCount) }}</dd></div>
            <div><dt>申请</dt><dd>{{ formatNumber(projectDetail.applicationCount) }}</dd></div>
          </dl>
          <p>{{ projectDetail.description || '项目发布方暂未提供更多说明。' }}</p>
          <strong :class="projectDetail.acceptingBid ? 'project-open' : 'project-closed'">{{ projectDetail.acceptingBid ? '当前可申请' : '项目已结束' }}</strong>
        </template>
      </div>
    </AppModal>
  </div>
</template>
